import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/api";
import { Upload, Image as ImageIcon, Film, Trash2, Edit3, X, Loader2, Download, Filter, Check, Tag, Camera, Users as UsersIcon, Share2, MessageCircle, Instagram, Copy } from "lucide-react";

const RATIO_OPTIONS = [
  { v: "9:16", label: "9:16 (dikey)" },
  { v: "4:3", label: "4:3 (yatay)" },
];

function authedUrl(itemId) {
  const token = localStorage.getItem("tk_token") || "";
  return `${API}/album/file/${itemId}?auth=${encodeURIComponent(token)}`;
}

function fmtRel(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
}

function canDelete(item, user) {
  if (user?.role === "admin") return true;
  if (item.uploader !== user?.username) return false;
  const ageH = (Date.now() - new Date(item.created_at).getTime()) / 3600000;
  return ageH <= 72;
}

export default function Album() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [cabins, setCabins] = useState([]);
  const [users, setUsers] = useState([]); // for tagging people
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ active: 0, total: 0 });
  const [filter, setFilter] = useState({ uploader: "", cabin_id: "", person: "" });
  const [editing, setEditing] = useState(null); // item being edited
  const [exportOpen, setExportOpen] = useState(false);
  const [selected, setSelected] = useState(new Set()); // for custom selection
  const [lightbox, setLightbox] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.uploader) params.uploader = filter.uploader;
      if (filter.cabin_id) params.cabin_id = filter.cabin_id;
      if (filter.person) params.person = filter.person;
      const [a, c] = await Promise.all([
        api.get("/album", { params }),
        api.get("/cabins").catch(() => ({ data: [] })),
      ]);
      setItems(Array.isArray(a.data) ? a.data : []);
      setCabins(Array.isArray(c.data) ? c.data : []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.uploader, filter.cabin_id, filter.person]);

  // Try to load users for tagging. Falls back gracefully.
  useEffect(() => {
    api.get("/users/public").then((r) => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => setUsers([]));
  }, []);

  const onPick = () => fileInputRef.current?.click();
  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading({ active: 0, total: files.length });
    let done = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/album/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) {
        // continue with others
      } finally {
        done++;
        setUploading({ active: done, total: files.length });
      }
    }
    setUploading({ active: 0, total: 0 });
    await load();
  };

  const remove = async (it) => {
    if (!window.confirm(`"${it.original_filename}" silinsin mi?`)) return;
    try {
      await api.delete(`/album/${it.id}`);
      setItems((arr) => arr.filter((x) => x.id !== it.id));
    } catch (e) {
      alert(e.response?.data?.detail || "Silinemedi");
    }
  };

  const saveTags = async (it, payload) => {
    try {
      await api.put(`/album/${it.id}/tags`, payload);
      setEditing(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const ns = new Set(s);
      if (ns.has(id)) ns.delete(id);
      else ns.add(id);
      return ns;
    });
  };

  const [shareModal, setShareModal] = useState(null); // { blob, name, url }

  const triggerDownload = async (queryStr, path = "/album/export") => {
    const token = localStorage.getItem("tk_token") || "";
    const isSlayt = path.includes("slideshow");
    try {
      const res = await fetch(`${API}${path}?${queryStr}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename=([^;]+)/);
      const name = (m ? m[1] : "album").replace(/"/g, "").trim();
      if (isSlayt) {
        // Show share modal instead of immediate download
        const url = URL.createObjectURL(blob);
        setShareModal({ blob, name, url });
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || "İndirilemedi");
    }
  };

  const userMap = useMemo(() => {
    const m = {};
    for (const u of users) m[u.username] = u.display_name;
    return m;
  }, [users]);

  return (
    <div className="space-y-4" data-testid="album-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-display font-semibold">Anı Albümü</h2>
          <p className="text-sm" style={{ color: "hsl(var(--text-2))" }}>
            Fotoğraf ve videolarınızı yükleyin. Kendi yüklediklerinizi 72 saat içinde silebilirsiniz.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setExportOpen(true)}
            className="tk-btn-ghost flex items-center gap-1.5"
            data-testid="album-export-btn"
            disabled={items.length === 0}
          >
            <Download className="w-4 h-4" /> İndir
          </button>
          <button onClick={onPick} className="tk-btn flex items-center gap-1.5" data-testid="album-upload-btn">
            <Upload className="w-4 h-4" /> Yükle
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => { onFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
            data-testid="album-file-input"
          />
        </div>
      </div>

      {uploading.total > 0 && (
        <div className="tk-card p-3 flex items-center gap-2 text-sm" data-testid="album-upload-progress">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#4992ff" }} />
          Yükleniyor: {uploading.active} / {uploading.total}
        </div>
      )}

      {/* Filters */}
      <div className="tk-card p-3 flex flex-wrap gap-2 items-center text-sm" data-testid="album-filters">
        <Filter className="w-4 h-4" style={{ color: "hsl(var(--text-2))" }} />
        <select className="tk-input !py-1.5" value={filter.uploader} onChange={(e) => setFilter((f) => ({ ...f, uploader: e.target.value }))}>
          <option value="">Tüm yükleyenler</option>
          {users.map((u) => <option key={u.username} value={u.username}>{u.display_name}</option>)}
        </select>
        <select className="tk-input !py-1.5" value={filter.cabin_id} onChange={(e) => setFilter((f) => ({ ...f, cabin_id: e.target.value }))}>
          <option value="">Tüm kabinler</option>
          {cabins.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="tk-input !py-1.5" value={filter.person} onChange={(e) => setFilter((f) => ({ ...f, person: e.target.value }))}>
          <option value="">Tüm kişiler</option>
          {users.map((u) => <option key={u.username} value={u.username}>{u.display_name}</option>)}
        </select>
        <button onClick={() => setFilter({ uploader: "", cabin_id: "", person: "" })} className="tk-btn-ghost !py-1.5 !px-2 text-xs">Temizle</button>
        <span className="ml-auto" style={{ color: "hsl(var(--text-2))" }}>
          {items.length} öğe · seçili: {selected.size}
        </span>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="tk-card p-6 flex items-center gap-2 text-sm" style={{ color: "hsl(var(--text-2))" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...
        </div>
      ) : items.length === 0 ? (
        <div className="tk-card p-6 text-center text-sm" style={{ color: "hsl(var(--text-2))" }}>
          Henüz öğe yok. <Camera className="inline w-4 h-4" /> Yükleye tıklayarak başla.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="album-grid">
          {items.map((it) => {
            const isSel = selected.has(it.id);
            return (
              <div key={it.id} className="tk-card p-0 relative overflow-hidden" data-testid={`album-item-${it.id}`}>
                <div
                  className="relative w-full bg-[hsl(var(--surface-2))] cursor-pointer"
                  style={{ aspectRatio: "1 / 1" }}
                  onClick={() => setLightbox(it)}
                >
                  {it.kind === "photo" ? (
                    <img
                      src={authedUrl(it.id)}
                      alt={it.original_filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: "#0b132b" }}>
                      <Film className="w-10 h-10 text-white/80" />
                    </div>
                  )}
                  <div
                    role="checkbox"
                    aria-checked={isSel}
                    onClick={(e) => { e.stopPropagation(); toggleSelect(it.id); }}
                    className="absolute top-1.5 left-1.5 w-6 h-6 rounded-full flex items-center justify-center shadow-md cursor-pointer"
                    style={{ backgroundColor: isSel ? "#4992ff" : "rgba(255,255,255,0.85)" }}
                    data-testid={`album-select-${it.id}`}
                  >
                    {isSel ? <Check className="w-3.5 h-3.5 text-white" /> : <span className="text-[10px]" style={{ color: "hsl(var(--text-2))" }}>○</span>}
                  </div>
                </div>
                <div className="p-2 space-y-1">
                  <div className="flex items-center justify-between gap-1">
                    <div className="text-[11px] truncate flex-1" title={it.uploader_display}>
                      <b>{it.uploader_display}</b> · <span style={{ color: "hsl(var(--text-2))" }}>{fmtRel(it.created_at)}</span>
                    </div>
                    <div className="flex gap-0.5">
                      <button onClick={() => setEditing(it)} className="tk-btn-ghost !p-1" title="Etiketle" data-testid={`album-edit-${it.id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      {canDelete(it, user) && (
                        <button onClick={() => remove(it)} className="tk-btn-danger !p-1" title="Sil" data-testid={`album-delete-${it.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 text-[10px]">
                    {it.cabin_id && (
                      <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
                        {cabins.find((c) => c.id === it.cabin_id)?.name || it.cabin_id}
                      </span>
                    )}
                    {(it.people || []).map((p) => (
                      <span key={p} className="px-1.5 py-0.5 rounded" style={{ backgroundColor: "#dbe9ff", color: "#4992ff" }}>
                        @{userMap[p] || p}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3" style={{ backgroundColor: "rgba(0,0,0,0.85)" }} onClick={() => setLightbox(null)} data-testid="album-lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-3 right-3 p-2 rounded-full bg-white/15 text-white">
            <X className="w-5 h-5" />
          </button>
          {lightbox.kind === "photo" ? (
            <img src={authedUrl(lightbox.id)} alt={lightbox.original_filename} className="max-h-full max-w-full object-contain" />
          ) : (
            <video src={authedUrl(lightbox.id)} controls autoPlay className="max-h-full max-w-full" />
          )}
        </div>
      )}

      {/* Edit Tags Modal */}
      {editing && (
        <EditTags
          item={editing}
          cabins={cabins}
          users={users}
          onClose={() => setEditing(null)}
          onSave={(p) => saveTags(editing, p)}
        />
      )}

      {/* Export wizard */}
      {exportOpen && (
        <ExportWizard
          onClose={() => setExportOpen(false)}
          cabins={cabins}
          users={users}
          selectedIds={selected}
          onTrigger={(qs, path) => { triggerDownload(qs, path); setExportOpen(false); }}
        />
      )}

      {/* Slideshow share modal */}
      {shareModal && (
        <SlaytShareModal
          data={shareModal}
          onClose={() => { URL.revokeObjectURL(shareModal.url); setShareModal(null); }}
        />
      )}
    </div>
  );
}

function EditTags({ item, cabins, users, onClose, onSave }) {
  const [cabinId, setCabinId] = useState(item.cabin_id || "");
  const [people, setPeople] = useState(item.people || []);
  const togglePerson = (u) => {
    setPeople((arr) => (arr.includes(u) ? arr.filter((x) => x !== u) : [...arr, u]));
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3" style={{ backgroundColor: "rgba(0,0,0,0.55)" }} onClick={onClose} data-testid="album-edit-modal">
      <div className="tk-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <Tag className="w-5 h-5" style={{ color: "#4992ff" }} />
          <div className="font-display font-semibold">Etiketle</div>
        </div>
        <label className="tk-label">Kabin</label>
        <select className="tk-input mb-3" value={cabinId} onChange={(e) => setCabinId(e.target.value)} data-testid="edit-cabin-select">
          <option value="">— Yok —</option>
          {cabins.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label className="tk-label">Kişiler</label>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {users.map((u) => {
            const active = people.includes(u.username);
            return (
              <button
                key={u.username}
                onClick={() => togglePerson(u.username)}
                className="px-2 py-1 rounded-full text-xs"
                style={{
                  backgroundColor: active ? "#4992ff" : "hsl(var(--surface-2))",
                  color: active ? "white" : "hsl(var(--text-2))",
                }}
                data-testid={`edit-person-${u.username}`}
              >
                {u.display_name}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="tk-btn-ghost text-sm">Vazgeç</button>
          <button onClick={() => onSave({ cabin_id: cabinId, people })} className="tk-btn text-sm" data-testid="edit-save-btn">Kaydet</button>
        </div>
      </div>
    </div>
  );
}

function ExportWizard({ onClose, cabins, users, selectedIds, onTrigger }) {
  const [target, setTarget] = useState(selectedIds.size > 0 ? "custom" : "all");
  const [cabinId, setCabinId] = useState("");
  const [person, setPerson] = useState("");
  const [fmt, setFmt] = useState("zip");
  const [ratio, setRatio] = useState("9:16");
  const [duration, setDuration] = useState(60);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const params = new URLSearchParams();
    params.set("target", target);
    if (target === "cabin") params.set("cabin_id", cabinId);
    if (target === "person") params.set("person", person);
    if (target === "custom") params.set("ids", Array.from(selectedIds).join(","));
    if (fmt === "slayt") {
      params.set("ratio", ratio);
      params.set("duration", String(duration));
      setBusy(true);
      try { await onTrigger(params.toString(), "/album/export/slideshow"); } finally { setBusy(false); }
    } else {
      params.set("fmt", fmt);
      params.set("ratio", ratio);
      onTrigger(params.toString(), "/album/export");
    }
  };

  const TARGETS = [
    { v: "mine", label: "Bireysel (yüklediklerim)" },
    { v: "cabin", label: "Kabin" },
    { v: "person", label: "Kişi (etiketli)" },
    { v: "all", label: "Toplu (hepsi)" },
    { v: "custom", label: `Özel seçim (${selectedIds.size} öğe)`, disabled: selectedIds.size === 0 },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3" style={{ backgroundColor: "rgba(0,0,0,0.55)" }} onClick={onClose} data-testid="album-export-modal">
      <div className="tk-card p-5 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <Download className="w-5 h-5" style={{ color: "#4992ff" }} />
          <div className="font-display font-semibold">Albüm İndir</div>
        </div>

        <div className="space-y-2 mb-4">
          <label className="tk-label">Ne indirilsin?</label>
          {TARGETS.map((t) => (
            <label key={t.v} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer border ${target === t.v ? "border-[#4992ff] bg-[#dbe9ff]" : "border-transparent bg-[hsl(var(--surface-2))]"}`} style={{ opacity: t.disabled ? 0.4 : 1 }}>
              <input type="radio" disabled={t.disabled} checked={target === t.v} onChange={() => setTarget(t.v)} data-testid={`export-target-${t.v}`} />
              <span className="text-sm">{t.label}</span>
            </label>
          ))}
        </div>

        {target === "cabin" && (
          <select className="tk-input mb-3" value={cabinId} onChange={(e) => setCabinId(e.target.value)} data-testid="export-cabin-select">
            <option value="">— Kabin seç —</option>
            {cabins.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        {target === "person" && (
          <select className="tk-input mb-3" value={person} onChange={(e) => setPerson(e.target.value)} data-testid="export-person-select">
            <option value="">— Kişi seç —</option>
            {users.map((u) => <option key={u.username} value={u.username}>{u.display_name}</option>)}
          </select>
        )}

        <label className="tk-label">Format</label>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <button onClick={() => setFmt("zip")} className={`px-2 py-2 rounded-lg border text-xs ${fmt === "zip" ? "border-[#4992ff] bg-[#dbe9ff]" : "border-transparent bg-[hsl(var(--surface-2))]"}`} data-testid="export-fmt-zip">
            ZIP paketi
          </button>
          <button onClick={() => setFmt("video")} className={`px-2 py-2 rounded-lg border text-xs ${fmt === "video" ? "border-[#4992ff] bg-[#dbe9ff]" : "border-transparent bg-[hsl(var(--surface-2))]"}`} data-testid="export-fmt-video">
            Basit slayt (MP4)
          </button>
          <button onClick={() => setFmt("slayt")} className={`px-2 py-2 rounded-lg border text-xs font-medium ${fmt === "slayt" ? "border-[#ff6b35] bg-[#fff0e8] text-[#ff6b35]" : "border-transparent bg-[hsl(var(--surface-2))]"}`} data-testid="export-fmt-slayt">
            Slayt Üret ✨
          </button>
        </div>

        {(fmt === "video" || fmt === "slayt") && (
          <>
            <label className="tk-label">Oran</label>
            <div className="flex gap-2 mb-3">
              {RATIO_OPTIONS.map((r) => (
                <button key={r.v} onClick={() => setRatio(r.v)} className={`px-3 py-1.5 rounded-lg text-sm ${ratio === r.v ? "bg-[#4992ff] text-white" : "bg-[hsl(var(--surface-2))] text-[hsl(var(--text-2))]"}`} data-testid={`export-ratio-${r.v.replace(":","x")}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </>
        )}

        {fmt === "slayt" && (
          <>
            <label className="tk-label">Hedef süre: <b>{duration} sn</b></label>
            <input
              type="range" min={15} max={120} step={5} value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full mb-3" data-testid="export-duration-slider"
            />
            <div className="rounded-lg p-3 mb-3 text-[11px] leading-relaxed" style={{ backgroundColor: "#fff7ee", color: "#7a4326" }}>
              ✨ <b>Otomatik slayt:</b> foto 2.5 sn · video kendi süresi (max 6 sn) · cross-dissolve & slide geçişler · alt-orta dinamik mini-harita (turuncu=geçmiş, beyaz=gelecek), klipler arası fade in/out.
              <br/>İşlem 30-90 sn sürebilir.
            </div>
          </>
        )}
        {fmt === "video" && (
          <p className="text-[11px] mb-3" style={{ color: "hsl(var(--text-2))" }}>
            Basit slayt: yalnız fotoğraflar, 3 sn'lik düz geçişler, harita yok.
          </p>
        )}

        <div className="flex gap-2 justify-end mt-2">
          <button onClick={onClose} className="tk-btn-ghost text-sm" disabled={busy}>Vazgeç</button>
          <button onClick={submit} disabled={busy} className="tk-btn text-sm flex items-center gap-1.5 disabled:opacity-60" data-testid="export-submit-btn">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Üretiliyor...</> : <><Download className="w-4 h-4" /> İndir</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function SlaytShareModal({ data, onClose }) {
  const { blob, name, url } = data;
  const [copied, setCopied] = useState(false);
  const SHARE_TEXT = "Mavi Yolculuk '26 anılarımdan bir slayt 🎬⛵";

  const canNativeShare = typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [new File([blob], name, { type: "video/mp4" })] });

  const downloadFile = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const nativeShare = async () => {
    try {
      const file = new File([blob], name, { type: "video/mp4" });
      await navigator.share({ files: [file], title: "Mavi Yolculuk '26 Slayt", text: SHARE_TEXT });
    } catch (e) {
      if (e?.name !== "AbortError") alert("Paylaşılamadı: " + e.message);
    }
  };

  const whatsappShare = () => {
    // Cannot directly attach video via wa.me — we download + open WA with text.
    downloadFile();
    setTimeout(() => {
      window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_TEXT + " (Az önce indirilen videoyu sohbete yükleyin 🎥)")}`, "_blank");
    }, 500);
  };

  const igShare = () => {
    downloadFile();
    alert("Video indirildi. Instagram > Story > Galerinden video seç → paylaş.");
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_TEXT);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3" style={{ backgroundColor: "rgba(0,0,0,0.7)" }} onClick={onClose} data-testid="slayt-share-modal">
      <div className="tk-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-semibold flex items-center gap-2">
            <Share2 className="w-5 h-5" style={{ color: "#ff6b35" }} /> Slayt Hazır 🎬
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-2))]" data-testid="slayt-share-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="rounded-xl overflow-hidden mb-4 bg-black" style={{ aspectRatio: "9/16", maxHeight: 360 }} data-testid="slayt-share-preview">
          <video src={url} controls autoPlay loop playsInline className="w-full h-full object-contain" />
        </div>

        <p className="text-sm mb-3 text-center" style={{ color: "hsl(var(--text-2))" }}>
          Tek dokunuşla paylaş veya cihazına indir.
        </p>

        <div className="grid grid-cols-2 gap-2 mb-2">
          {canNativeShare && (
            <button onClick={nativeShare} className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium text-white col-span-2" style={{ backgroundColor: "#ff6b35" }} data-testid="slayt-share-native">
              <Share2 className="w-4 h-4" /> Paylaş (Telefonum)
            </button>
          )}
          <button onClick={whatsappShare} className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium text-white" style={{ backgroundColor: "#25d366" }} data-testid="slayt-share-whatsapp">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </button>
          <button onClick={igShare} className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium text-white" style={{ background: "linear-gradient(135deg, #f58529, #dd2a7b, #8134af)" }} data-testid="slayt-share-instagram">
            <Instagram className="w-4 h-4" /> Instagram
          </button>
          <button onClick={downloadFile} className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium tk-btn-ghost" data-testid="slayt-share-download">
            <Download className="w-4 h-4" /> İndir
          </button>
          <button onClick={copyText} className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium tk-btn-ghost" data-testid="slayt-share-copy">
            <Copy className="w-4 h-4" /> {copied ? "Kopyalandı!" : "Metni Kopyala"}
          </button>
        </div>

        <p className="text-[11px] mt-2 text-center" style={{ color: "hsl(var(--text-2))" }}>
          {canNativeShare ? "Mobilde 'Paylaş' butonu uygulamalara doğrudan gönderir." : "İlk önce videoyu indir, sonra istediğin uygulamaya yükle."}
        </p>
      </div>
    </div>
  );
}
